import {
  Children,
  cloneElement,
  createContext,
  memo,
  useContext,
  useEffect,
  useState,
} from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import _ from 'lodash';
import clsx from 'clsx';

const initialLetters = (text) => {
  const result = text
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
  return result;
};

const stickyRightStyled = 'md:sticky md:right-0 md:border-l-1';
const stickyLeftStyled = 'md:sticky md:left-0 md:border-r-1 z-50';

const TableContext = createContext({});
const { Provider } = TableContext;

const WithProvider = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  return <Provider value={{ isCollapsed, setIsCollapsed }}>{children}</Provider>;
};

const Collapsed = ({ nestedData }) => {
  const { isCollapsed } = useContext(TableContext);
  const [metadata, setMetadata] = useState([]);

  const transformMetadata = (metadataField = {}) => {
    const transformedMetadata = Object.keys(metadataField).map((key) => ({
      keyObj: key,
      valueObj: metadataField[key],
    }));
    setMetadata(transformedMetadata);
  };

  useEffect(() => {
    if (!nestedData) {
      return;
    }
    transformMetadata(nestedData);
  }, [nestedData]);

  return (
    <TableRow>
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
        <Collapse in={isCollapsed} timeout="auto" unmountOnExit>
          <Box sx={{ margin: 1 }}>
            {metadata.length > 0 ? (
              <Table size="small" aria-label="metadata">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '100%' }}>Clave</TableCell>
                    <TableCell sx={{ width: '100%' }}>Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metadata.map((rowItem, index) => (
                    <TableRow key={`nestedtable_${rowItem.keyObj}_${index}`}>
                      <TableCell component="th" scope="row">
                        {rowItem.keyObj}
                      </TableCell>
                      <TableCell>{rowItem.valueObj}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-auto w-full py-16 flex-col justify-center items-center">
                <Typography variant="subtitle1" gutterBottom>
                  No hay datos disponibles
                </Typography>
              </div>
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

/**
 * ComposedTableFlat
 *
 * Componente de tabla plana con soporte para filas colapsables, selección y renderizado personalizado de columnas.
 *
 * @component
 * @param {boolean} [selectable=false] - Si las filas son seleccionables
 * @param {function} [onRowClick] - Callback al hacer click en una fila (índice, item)
 * @param {React.ReactNode} children - Columnas a renderizar (usualmente ComposedTableFlat.Column)
 * @param {Array<object>} [headers=[]] - Definición de encabezados de la tabla
 * @param {Array<object>} data - Datos a mostrar en la tabla
 * @param {function} [metadata] - Función para obtener metadata de la fila
 * @param {boolean} [isLoading] - Si la tabla está en estado de carga
 * @param {string} [size="small"] - Tamaño de la tabla
 * @param {string|number} [maxHeight] - Altura máxima del contenedor
 * @param {string} [headerTextColor] - Color del texto del encabezado
 * @param {string} [headerBgColor] - Color de fondo del encabezado
 * @returns {JSX.Element}
 */
export const ComposedTableFlat = ({
  selectable = false,
  onRowClick = () => {},
  children,
  headers = [],
  data,
  metadata = () => {},
  isLoading,
  size = 'small',
  maxHeight,
  headerTextColor,
  headerBgColor,
}) => {
  return (
    <TableContainer
      sx={{
        borderRadius: '0.4rem',
        position: 'relative',
        maxHeight: maxHeight || '180rem',
        minHeight: '16rem',
      }}
    >
      <Table aria-label="composed table component" size={size} stickyHeader>
        <TableHead>
          <TableRow>
            {headers.map(
              (
                { title, width, minWidth, maxWidth, fixedRight, fixedLeft, align = 'center' },
                index
              ) => (
                <TableCell
                  key={`${title}_${index}`}
                  align={align}
                  style={{
                    width,
                    minWidth,
                    maxWidth,
                    color: headerTextColor || undefined,
                    backgroundColor: headerBgColor || undefined,
                  }}
                  className={`${
                    fixedRight && !fixedLeft
                      ? `${stickyRightStyled} md:border-l-neutral-700 md:shadow-md`
                      : ''
                  } ${
                    !fixedRight && fixedLeft
                      ? `${stickyLeftStyled} md:border-r-neutral-700 md:shadow-md`
                      : ''
                  }`}
                >
                  {title}
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item, indexRow) => (
            <WithProvider key={indexRow}>
              <TableRow
                onClick={() => onRowClick(indexRow, item)}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  cursor: selectable ? 'pointer' : undefined,
                }}
              >
                {Children.map(children, (child) => {
                  return cloneElement(child, { item });
                })}
              </TableRow>
              <Collapsed nestedData={_.has(item, 'metadata') ? item.metadata : undefined} />
            </WithProvider>
          ))}
        </TableBody>
      </Table>
      {!data.length > 0 && !isLoading && (
        <div
          className="bg-slate-950 h-full w-full left-0 top-0 bg-opacity-50 flex flex-auto justify-center items-end"
          style={{ position: 'absolute' }}
        >
          <div className="mt-16 mb-24">
            <Typography variant="body2">No hay datos que mostrar</Typography>
          </div>
        </div>
      )}
      {isLoading && (
        <div
          className="h-full w-full left-0 top-0 flex flex-auto justify-center items-center"
          style={{
            position: 'absolute',
            backgroundColor: 'rgba(56, 56, 56, 0.22)',
            backdropFilter: 'blur(1px)',
            zIndex: 100,
          }}
        >
          <div className="mt-16">
            <CircularProgress sx={{ color: '#FEFEFE' }} size={60} />
          </div>
        </div>
      )}
    </TableContainer>
  );
};

const Column = ({ fixedRight, fixedLeft, content = () => {}, item }) => {
  const handleRenderCol = () => {
    const rendered = content(item);
    return rendered;
  };

  return (
    <TableCell
      align="center"
      className={`${
        fixedRight && !fixedLeft ? `${stickyRightStyled} md:border-l-neutral-400 md:shadow-md` : ''
      } ${
        !fixedRight && fixedLeft ? `${stickyLeftStyled} md:border-r-neutral-400 md:shadow-md` : ''
      } `}
      sx={{ backgroundColor: (_theme) => _theme.palette.background.default }}
    >
      {handleRenderCol()}
    </TableCell>
  );
};

const NestedButton = () => {
  const { isCollapsed, setIsCollapsed } = useContext(TableContext);
  return (
    <IconButton aria-label="expand row" size="small" onClick={() => setIsCollapsed(!isCollapsed)}>
      {!isCollapsed ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
    </IconButton>
  );
};

const Thumbail = ({ baseUri, path, alt, name, color, className, style, square = false }) => {
  return (
    <div className="flex justify-center items-center">
      <Avatar
        alt={alt}
        sx={{ bgcolor: color || undefined }}
        variant={square ? 'square' : undefined}
        src={`${baseUri || ''}${baseUri ? '/' : ''}${path}`}
        style={style}
        className={clsx(className)}
      >
        {name !== undefined && initialLetters(name)}
      </Avatar>
    </div>
  );
};

const Preview = ({ baseUri, path = '', alt, width, height, aspectRatio, style, className }) => {
  return typeof path !== 'string' || path === undefined || path.trim().length === 0 ? (
    <div className={clsx('flex flex-row justify-center items-center', className)}>
      <HideImageOutlinedIcon />
    </div>
  ) : (
    <div
      className={clsx('flex flex-row justify-center', className)}
      style={{ width, height, aspectRatio, ...style }}
    >
      <img
        className="w-full object-contain"
        alt={alt}
        src={`${baseUri || ''}${baseUri ? '/' : ''}${path}`}
      />
    </div>
  );
};

/**
 * Column
 *
 * Subcomponente para renderizar una celda personalizada en la tabla.
 *
 * @component
 * @param {boolean} [fixedRight] - Si la columna es sticky a la derecha
 * @param {boolean} [fixedLeft] - Si la columna es sticky a la izquierda
 * @param {function} [content] - Función que retorna el contenido de la celda
 * @param {object} item - Objeto de datos de la fila
 * @returns {JSX.Element}
 */
ComposedTableFlat.Column = Column;

/**
 * NestedButton
 *
 * Botón para expandir o contraer la fila colapsable.
 *
 * @component
 * @returns {JSX.Element}
 */
ComposedTableFlat.NestedButton = memo(NestedButton);

/**
 * Thumbail
 *
 * Subcomponente para mostrar un avatar o iniciales en la tabla.
 *
 * @component
 * @param {string} [baseUri] - Ruta base para la imagen
 * @param {string} [path] - Ruta de la imagen
 * @param {string} [alt] - Texto alternativo
 * @param {string} [name] - Nombre para iniciales
 * @param {string} [color] - Color de fondo
 * @param {string} [className] - Clases CSS adicionales
 * @param {object} [style] - Estilos adicionales
 * @param {boolean} [square=false] - Si el avatar es cuadrado
 * @returns {JSX.Element}
 */
ComposedTableFlat.Thumbail = Thumbail;

/**
 * Preview
 *
 * Subcomponente para mostrar una previsualización de imagen en la tabla.
 *
 * @component
 * @param {string} [baseUri] - Ruta base para la imagen
 * @param {string} [path] - Ruta de la imagen
 * @param {string} [alt] - Texto alternativo
 * @param {string|number} [width] - Ancho de la previsualización
 * @param {string|number} [height] - Alto de la previsualización
 * @param {string|number} [aspectRatio] - Aspect ratio de la previsualización
 * @param {object} [style] - Estilos adicionales
 * @param {string} [className] - Clases CSS adicionales
 * @returns {JSX.Element}
 */
ComposedTableFlat.Preview = Preview;
